// services/saveInterestedParties.ts

import { auth, db, firebaseImport } from '../../config/main_config';
import { waitForCompanyID } from './waitForCompanyID.util';
import { createToast } from './createToast.util.js';
import { closeModal } from './closeModal.util';
import { InterestedParty, InterestedPartiesRegister } from '../../../../interfaces/interestedParties.interface';
import { getFormattedTimestamp } from './getFormattedTimestamp.util';

function getNextPartyID(existingParties: InterestedPartiesRegister | undefined): string {
    if (!existingParties || Object.keys(existingParties).length === 0) {
        return "PTY-1"; // Default party ID if there are no existing parties
    }

    const partyIDs: number[] = Object.keys(existingParties).map(partyID => {
        // Regular expression to extract numeric part from either "party<number>" or "PTY-<number>"
        const match = partyID.match(/(?:party|PTY)-?(\d+)/i);
        if (match && match[1]) {
            const idNumber = parseInt(match[1], 10);
            return isNaN(idNumber) ? 0 : idNumber;
        }
        return 0; // Default to 0 if pattern does not match
    });

    const maxID = Math.max(...partyIDs);
    return `PTY-${maxID + 1}`;
}

async function updateInterestedPartiesHistoryCollection(companyID: string, currentRegister: InterestedPartiesRegister): Promise<void> {
    const timestamp = getFormattedTimestamp();
    const historyRef = db.collection(`/customers/${companyID}/product/managementSystemGuide/interestedPartiesRegister/register/history`).doc(timestamp);

    try {
        await historyRef.set(currentRegister);
        console.log(`Interested Parties register snapshot saved to history as ${timestamp}`);
    } catch (error) {
        console.error('Error saving Interested Parties register snapshot to history:', error);
    }
}

async function saveNewInterestedParty(companyID: string): Promise<void> {
    // Helper function to safely get input values
    const getInputValue = (id: string): string => {
        const element = document.getElementById(id) as HTMLInputElement | null;
        return element?.value.trim() || '';
    };

    const dateAddedInput = getInputValue('dateAdded_new');
    const party = getInputValue('party_new');
    const requirement = getInputValue('partyRequirement_new');
    const requirementsAddressedThroughISMS = getInputValue('outcomeOnManagementSystem_new');
    const updatedBy = auth.currentUser.uid; // Ensure this field is set appropriately
    const frameworksElement = document.getElementById('frameworks_new') as HTMLInputElement;
    // Use type assertion to handle the window.selectedControls property
    const frameworks = 'selectedControls' in window ? (window as any).selectedControls : frameworksElement.value.split(',').map(fw => fw.trim()).filter(fw => fw !== '');

    const documentRef = db.doc(`/customers/${companyID}/product/managementSystemGuide/interestedPartiesRegister/register`);

    try {
        let currentRegister: InterestedPartiesRegister = {};
        // Fetch the current state of the register
        const docSnapshot = await documentRef.get();
        if (!docSnapshot.exists) {
            // Pull data from old ismsManual path (/customers/2ulbY4vGgQ3UUtKpaMvX/product/policyPackages/ISO27001keyDocuments/ismsManual) to get existing parties
            const oldParties = await waitForCompanyID()
                ?.then(companyID => db.doc(`/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`).get())
                .then(doc => doc.data()?.partiesInterested || [])
                .catch(error => console.error('Error pulling old Interested Parties from ismsManual:', error));

            // Merge the new party with the old parties
            currentRegister = { ...oldParties, ...currentRegister };

            // Update the current register in Firestore
            await documentRef.set(currentRegister, { merge: true });

            console.log('Interested Parties register updated successfully with old parties:', oldParties);
        } else if (docSnapshot.exists) {
            const data = docSnapshot.data();
            currentRegister = data as InterestedPartiesRegister;

        }

        let newPartyID = "PTY-1"; // Default party ID
        // Get the next party ID based on existing parties
        newPartyID = getNextPartyID(currentRegister);

        // Save the current register to the history subcollection before adding the new party
        await updateInterestedPartiesHistoryCollection(companyID, currentRegister);

        const newParty: InterestedParty = {
            dateAdded: dateAddedInput ? firebaseImport.firestore.Timestamp.fromDate(new Date()) : undefined,
            dateUpdated: firebaseImport.firestore.Timestamp.fromDate(new Date()),
            party,
            requirement,
            requirementsAddressedThroughISMS,
            frameworks,
            updatedBy
        };

        // Add the new party to the register
        await documentRef.set(
            {
                [newPartyID]: newParty
            },
            { merge: true }
        );
        // Save the new party to the old ismsManual path (if available)
        let oldIsmsManualPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
        if (oldIsmsManualPath) {
            currentRegister[newPartyID] = newParty;
            await db.doc(oldIsmsManualPath).update({
                partiesInterested: currentRegister
            });
        }

        console.log('New Interested Party added successfully:', newParty);

    } catch (error) {
        console.error('Error adding new Interested Party:', error);
    }
}

export async function addNewInterestedParty(event: Event): Promise<void> {
    event.preventDefault();

    const companyID = await waitForCompanyID(); // Assume this function fetches the current company ID

    if (companyID) {
        // Helper function to safely get input elements
        const getInputElement = (id: string): HTMLInputElement | null => {
            return document.getElementById(id) as HTMLInputElement | null;
        };

        // Get references to form fields before saving
        const dateAddedElement = getInputElement('dateAdded_new');
        const partyElement = getInputElement('party_new');
        const partyRequirementElement = getInputElement('partyRequirement_new');
        const outcomeElement = getInputElement('outcomeOnManagementSystem_new');
        const frameworksElement = getInputElement('frameworks_new');

        await saveNewInterestedParty(companyID);
        
        // Reset form fields after successful save
        if (dateAddedElement) dateAddedElement.value = '';
        if (partyElement) partyElement.value = '';
        if (partyRequirementElement) partyRequirementElement.value = '';
        if (outcomeElement) outcomeElement.value = '';
        if (frameworksElement) frameworksElement.value = '';
        
        // Reset selected controls
        // Use type assertion for the window.selectedControls property
        if (typeof window !== 'undefined' && 'selectedControls' in window) {
            (window as any).selectedControls = [];
        }
        
        // Close the modal
        closeModal(null, true);
        createToast({ message: 'Interested Party Added successfully.' });
    } else { alert('Error: Company ID not found.'); }
}